import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { PROJECT_EXPENSES_KEY } from './constants';
import { getProjectExpense } from './projectExpenses';
import { FormOptionProjectExpense } from './projectExpenses.types';
import { restOptionToFormOption } from '@/api/REST/utils';
import { formatCustomerDisplayName } from '@/api/typesense/customers';
import { formatEmployeeDisplayName } from '@/api/typesense/employees';
import { formatProjectDisplayName } from '@/api/typesense/projects';

export const useGetProjectExpense = (id: number) => {
  const { data, ...restProps } = useQuery({
    queryKey: [PROJECT_EXPENSES_KEY, id],
    queryFn: () => getProjectExpense(id),
    enabled: !!id,
  });

  const formOptionMassagedData: FormOptionProjectExpense | undefined = useMemo(
    () =>
      !data
        ? undefined
        : {
            ...data,
            project: restOptionToFormOption(data.project, (p) =>
              formatProjectDisplayName(p.name, p.number),
            ),
            customer: restOptionToFormOption(data.customer, (c) =>
              formatCustomerDisplayName(c.name, c.number),
            ),
            currency: {
              id: data.currencyId.toString(),
              value: data.currency.currencyCode,
              isoCode: data.currency.currencyCode,
            },
            projectCurrency: {
              id: data.projectCurrencyId.toString(),
              value: data.projectCurrency.currencyCode,
              isoCode: data.projectCurrency.currencyCode,
            },
            expenseType: restOptionToFormOption(data.expenseType),
            employee: restOptionToFormOption(
              {
                ...data.employee,
                id: data.employee.userId, // to align with Typesense, the userId is used as the id (SourceId in Typesense)
              },
              (e) => formatEmployeeDisplayName(e.name, e.initials),
            ),
            paymentMethod: restOptionToFormOption(data.paymentMethod),
            projectSubContract: restOptionToFormOption(data.projectSubContract),
            supplier: data.supplier
              ? { id: data.supplier.id.toString(), value: data.supplier.name }
              : undefined,
          },
    [data],
  );

  return { data: formOptionMassagedData, ...restProps };
};
